import type {
    McpClientSessionInfo,
    PluginInfo,
    SessionSummary,
    SidecarInfo,
} from "@magic-md/engine-client/protocol";
import { create } from "zustand";
import type { EngineStatus } from "../platform";
import { getPlatformService } from "../platform";
import { PLATFORM_CONNECTION_CHANGED_EVENT } from "../platform/engine-proxy";
import { getNotificationBus } from "./notification-bus";

const ENGINE_OVERVIEW_SCOPES = [
    "status",
    "plugins",
    "sessions",
    "sidecars",
    "mcp",
] as const;

export type EngineOverviewScope = (typeof ENGINE_OVERVIEW_SCOPES)[number];

interface EngineOverviewState {
    status: EngineStatus;
    plugins: PluginInfo[];
    sessions: SessionSummary[];
    sidecars: SidecarInfo[];
    mcpClients: McpClientSessionInfo[];
    loading: boolean;
    loadingScopes: Record<EngineOverviewScope, boolean>;
    error: string | null;
    lastUpdatedAt: number | null;
    retainScopes: (scopes: readonly EngineOverviewScope[]) => () => void;
    refreshNow: (scopes?: readonly EngineOverviewScope[]) => Promise<void>;
    scheduleRefresh: (
        scopes?: readonly EngineOverviewScope[],
        delayMs?: number,
    ) => void;
    closeSession: (sessionId: string) => Promise<void>;
}

type OverviewScopeResultMap = {
    status: EngineStatus;
    plugins: PluginInfo[];
    sessions: SessionSummary[];
    sidecars: SidecarInfo[];
    mcp: McpClientSessionInfo[];
};

const DEFAULT_STATUS: EngineStatus = {
    alive: false,
    ready: false,
};

const REFRESH_COALESCE_MS = 80;

let bindingsInstalled = false;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshPromise: Promise<void> | null = null;
const currentRefreshScopes = new Set<EngineOverviewScope>();
const retainedScopeCounts = new Map<EngineOverviewScope, number>();
const loadingScopeSet = new Set<EngineOverviewScope>();
const pendingRefreshScopes = new Set<EngineOverviewScope>();
const scheduledRefreshScopes = new Set<EngineOverviewScope>();
const bindingCleanupFns: Array<() => void> = [];
let windowConnectionHandler: (() => void) | null = null;

function createEmptyLoadingScopes(): Record<EngineOverviewScope, boolean> {
    return {
        status: false,
        plugins: false,
        sessions: false,
        sidecars: false,
        mcp: false,
    };
}

function resetOverviewData() {
    useEngineOverviewStore.setState({
        status: DEFAULT_STATUS,
        plugins: [],
        sessions: [],
        sidecars: [],
        mcpClients: [],
        loading: false,
        loadingScopes: createEmptyLoadingScopes(),
        error: null,
        lastUpdatedAt: Date.now(),
    });
}

function normalizeScopes(
    scopes?: readonly EngineOverviewScope[],
): EngineOverviewScope[] {
    if (!scopes || scopes.length === 0) {
        return [];
    }
    const requested = new Set<EngineOverviewScope>(scopes);
    return ENGINE_OVERVIEW_SCOPES.filter((scope) => requested.has(scope));
}

function getRetainedScopes(): EngineOverviewScope[] {
    return ENGINE_OVERVIEW_SCOPES.filter(
        (scope) => (retainedScopeCounts.get(scope) ?? 0) > 0,
    );
}

function resolveRequestedScopes(
    scopes?: readonly EngineOverviewScope[],
): EngineOverviewScope[] {
    const normalized = normalizeScopes(scopes);
    if (normalized.length > 0) {
        return normalized;
    }
    return getRetainedScopes();
}

function resolveRetainedScopes(
    scopes: readonly EngineOverviewScope[],
): EngineOverviewScope[] {
    const normalized = normalizeScopes(scopes);
    return normalized.filter((scope) => (retainedScopeCounts.get(scope) ?? 0) > 0);
}

function setScopesLoading(
    scopes: readonly EngineOverviewScope[],
    loading: boolean,
): void {
    if (scopes.length === 0) {
        return;
    }

    const nextLoadingScopes = {
        ...useEngineOverviewStore.getState().loadingScopes,
    };

    for (const scope of scopes) {
        if (loading) {
            loadingScopeSet.add(scope);
        } else {
            loadingScopeSet.delete(scope);
        }
        nextLoadingScopes[scope] = loading;
    }

    useEngineOverviewStore.setState({
        loading: loadingScopeSet.size > 0,
        loadingScopes: nextLoadingScopes,
    });
}

async function fetchScopeResult(
    scope: EngineOverviewScope,
): Promise<OverviewScopeResultMap[EngineOverviewScope]> {
    const engine = getPlatformService().engine;
    switch (scope) {
        case "status":
            return await engine.getEngineStatus();
        case "plugins":
            return (await engine.pluginList()).plugins;
        case "sessions":
            return (await engine.sessionList()).sessions;
        case "sidecars":
            return (await engine.sidecarList()).sidecars;
        case "mcp":
            return (await engine.mcpStatus?.())?.clients ?? [];
    }
}

function scopeErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

async function performRefresh(scopes: EngineOverviewScope[]): Promise<void> {
    if (scopes.length === 0) {
        return;
    }

    setScopesLoading(scopes, true);
    useEngineOverviewStore.setState({ error: null });

    try {
        const settled = await Promise.allSettled(
            scopes.map(async (scope) => ({
                scope,
                value: await fetchScopeResult(scope),
            })),
        );

        const nextState: Partial<EngineOverviewState> = {
            error: null,
            lastUpdatedAt: Date.now(),
        };

        let firstError: string | null = null;
        let refreshedStatus: EngineStatus | null = null;

        for (const result of settled) {
            if (result.status === "fulfilled") {
                const { scope, value } = result.value;
                switch (scope) {
                    case "status":
                        refreshedStatus = value as EngineStatus;
                        nextState.status = refreshedStatus;
                        break;
                    case "plugins":
                        nextState.plugins = value as PluginInfo[];
                        break;
                    case "sessions":
                        nextState.sessions = value as SessionSummary[];
                        break;
                    case "sidecars":
                        nextState.sidecars = value as SidecarInfo[];
                        break;
                    case "mcp":
                        nextState.mcpClients = value as McpClientSessionInfo[];
                        break;
                }
                continue;
            }

            if (!firstError) {
                firstError = scopeErrorMessage(result.reason);
            }
        }

        if (refreshedStatus && !refreshedStatus.alive) {
            if (scopes.includes("plugins")) {
                nextState.plugins = [];
            }
            if (scopes.includes("sessions")) {
                nextState.sessions = [];
            }
            if (scopes.includes("sidecars")) {
                nextState.sidecars = [];
            }
            if (scopes.includes("mcp")) {
                nextState.mcpClients = [];
            }
        } else if (refreshedStatus && !refreshedStatus.ready) {
            if (scopes.includes("sessions")) {
                nextState.sessions = [];
            }
            if (scopes.includes("sidecars")) {
                nextState.sidecars = [];
            }
            if (scopes.includes("mcp")) {
                nextState.mcpClients = [];
            }
        }

        if (firstError) {
            nextState.error = firstError;
        }

        useEngineOverviewStore.setState(nextState);
    } catch (error) {
        useEngineOverviewStore.setState({
            error: scopeErrorMessage(error),
            lastUpdatedAt: Date.now(),
        });
    } finally {
        setScopesLoading(scopes, false);
    }
}

function enqueueRefresh(scopes: readonly EngineOverviewScope[]): void {
    for (const scope of scopes) {
        if (currentRefreshScopes.has(scope)) {
            continue;
        }
        pendingRefreshScopes.add(scope);
    }
}

async function drainRefreshQueue(): Promise<void> {
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = (async () => {
        while (pendingRefreshScopes.size > 0) {
            const batch = ENGINE_OVERVIEW_SCOPES.filter((scope) =>
                pendingRefreshScopes.has(scope),
            );
            pendingRefreshScopes.clear();
            currentRefreshScopes.clear();
            for (const scope of batch) {
                currentRefreshScopes.add(scope);
            }
            await performRefresh(batch);
            currentRefreshScopes.clear();
        }
    })();

    try {
        await refreshPromise;
    } finally {
        currentRefreshScopes.clear();
        refreshPromise = null;
    }
}

function scheduleEngineOverviewRefresh(
    scopes?: readonly EngineOverviewScope[],
    delayMs = REFRESH_COALESCE_MS,
): void {
    const requestedScopes = resolveRequestedScopes(scopes);
    if (requestedScopes.length === 0) {
        return;
    }

    for (const scope of requestedScopes) {
        scheduledRefreshScopes.add(scope);
    }

    if (refreshTimer) {
        return;
    }

    refreshTimer = setTimeout(() => {
        refreshTimer = null;
        const batch = ENGINE_OVERVIEW_SCOPES.filter((scope) =>
            scheduledRefreshScopes.has(scope),
        );
        scheduledRefreshScopes.clear();
        enqueueRefresh(batch);
        void drainRefreshQueue();
    }, delayMs);
}

function scheduleRetainedRefresh(
    scopes: readonly EngineOverviewScope[],
    delayMs = REFRESH_COALESCE_MS,
): void {
    const retainedScopes = resolveRetainedScopes(scopes);
    if (retainedScopes.length === 0) {
        return;
    }
    scheduleEngineOverviewRefresh(retainedScopes, delayMs);
}

function installOverviewBindings() {
    if (bindingsInstalled) return;
    bindingsInstalled = true;

    const bus = getNotificationBus();
    const refreshImmediately = () =>
        scheduleEngineOverviewRefresh(undefined, 0);

    bindingCleanupFns.push(
        bus.subscribe("event/session.created", () => {
            scheduleRetainedRefresh(["sessions"]);
        }),
    );
    bindingCleanupFns.push(
        bus.subscribe("event/session.closed", () => {
            scheduleRetainedRefresh(["sessions"]);
        }),
    );
    bindingCleanupFns.push(
        bus.subscribe("event/sidecar.spawned", () => {
            scheduleRetainedRefresh(["sidecars"]);
        }),
    );
    bindingCleanupFns.push(
        bus.subscribe("event/sidecar.stopped", () => {
            scheduleRetainedRefresh(["sidecars"]);
        }),
    );
    bindingCleanupFns.push(
        bus.subscribe("event/sidecar.restarted", () => {
            scheduleRetainedRefresh(["sidecars"]);
        }),
    );
    bindingCleanupFns.push(
        bus.subscribe("event/transport.reconnected", refreshImmediately),
    );
    bindingCleanupFns.push(
        bus.subscribe("event/mcp.statusChanged", () => {
            scheduleRetainedRefresh(["mcp"]);
        }),
    );

    if (typeof window !== "undefined") {
        windowConnectionHandler = refreshImmediately;
        window.addEventListener(
            PLATFORM_CONNECTION_CHANGED_EVENT,
            windowConnectionHandler,
        );
    }
}

export const useEngineOverviewStore = create<EngineOverviewState>((set) => ({
    status: DEFAULT_STATUS,
    plugins: [],
    sessions: [],
    sidecars: [],
    mcpClients: [],
    loading: false,
    loadingScopes: createEmptyLoadingScopes(),
    error: null,
    lastUpdatedAt: null,
    retainScopes: (scopes) => {
        installOverviewBindings();
        const requestedScopes = normalizeScopes(scopes);
        const newlyRetainedScopes: EngineOverviewScope[] = [];

        for (const scope of requestedScopes) {
            const currentCount = retainedScopeCounts.get(scope) ?? 0;
            if (currentCount === 0) {
                newlyRetainedScopes.push(scope);
            }
            retainedScopeCounts.set(scope, currentCount + 1);
        }

        if (newlyRetainedScopes.length > 0) {
            scheduleEngineOverviewRefresh(newlyRetainedScopes, 0);
        }

        return () => {
            for (const scope of requestedScopes) {
                const currentCount = retainedScopeCounts.get(scope) ?? 0;
                if (currentCount <= 1) {
                    retainedScopeCounts.delete(scope);
                } else {
                    retainedScopeCounts.set(scope, currentCount - 1);
                }
            }
        };
    },
    refreshNow: async (scopes) => {
        installOverviewBindings();
        const requestedScopes = resolveRequestedScopes(scopes);
        if (requestedScopes.length === 0) {
            return;
        }
        enqueueRefresh(requestedScopes);
        await drainRefreshQueue();
    },
    scheduleRefresh: (scopes, delayMs) => {
        installOverviewBindings();
        scheduleEngineOverviewRefresh(scopes, delayMs);
    },
    closeSession: async (sessionId) => {
        await getPlatformService().engine.sessionClose(sessionId);
        set((state) => ({
            sessions: state.sessions.filter(
                (session) => session.sessionId !== sessionId,
            ),
            lastUpdatedAt: Date.now(),
        }));
        scheduleEngineOverviewRefresh(["sessions"], 0);
    },
}));

export function resetEngineOverviewStore(): void {
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
    refreshPromise = null;
    retainedScopeCounts.clear();
    loadingScopeSet.clear();
    currentRefreshScopes.clear();
    pendingRefreshScopes.clear();
    scheduledRefreshScopes.clear();

    while (bindingCleanupFns.length > 0) {
        bindingCleanupFns.pop()?.();
    }
    if (windowConnectionHandler && typeof window !== "undefined") {
        window.removeEventListener(
            PLATFORM_CONNECTION_CHANGED_EVENT,
            windowConnectionHandler,
        );
    }
    windowConnectionHandler = null;
    bindingsInstalled = false;

    resetOverviewData();
}
